import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { marked } from "marked";
import html2pdf from "html2pdf.js";

import { RootState } from "../../../store";
import { updateDashboardFields } from "../../../features/common/commonSlice";
import MeetingSummary from "../../restricted/previousMeetings/MeetingSummary";
import PanchayatiRajLogo from "../../../assets/images/PanchayatiRajLogo.png";
import Wrapper from "../../../assets/wrappers/PreviousMeetingSingle";

const MultilingualDashboard3: React.FC<{ finYear: string; meetingType: any }> = ({
    finYear,
    meetingType,
}) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const dispatch = useDispatch();

    // get multilingual dashboard data from redux
    const multilingual = useSelector(
        (state: RootState) => state.common.dashboardData["Multilingual Coverage"]
    );

    const [data, setData] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [summaryHtml, setSummaryHtml] = useState({
        englishSummary: "",
        regionalSummary: "",
    });
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const [storageData, setStorageData] = useState<any>(() => {
        const saved = localStorage.getItem("loginDetails");
        try {
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            console.error("Failed to parse loginDetails from localStorage:", e);
            return {};
        }
    });

    // fetch meeting details using taskId from redux
    useEffect(() => {
        if (!multilingual?.taskId) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${baseUrl}api/meeting-details/${multilingual.taskId}`);
                if (response.data.msg_code === 200) {
                    setData(response.data);
                } else {
                    setData(null)
                }
            } catch (err) {
                setError("Failed to fetch meeting details");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [multilingual?.taskId]);

    // convert markdown to HTML for summaries
    const normalizeMarkdown = (text: string) =>
        text.replace(/\*\*\s+</g, "**<").replace(/>\s+\*\*/g, ">**");

    useEffect(() => {
        const convertSummary = async () => {
            if (data?.english_minutes || data?.translated_minutes) {
                const regionalHtml = data?.translated_minutes
                    ? await marked.parse(normalizeMarkdown(data.translated_minutes))
                    : "";

                const englishHtml = data?.english_minutes
                    ? await marked.parse(normalizeMarkdown(data.english_minutes))
                    : "";

                setSummaryHtml({
                    englishSummary: englishHtml,
                    regionalSummary: regionalHtml,
                });
            }
        };
        convertSummary();
    }, [data?.english_minutes, data?.translated_minutes]);

    // export meeting summary to PDF
    const handleExportPDF = (htmlContent: string, fileName: string = "Minutes_of_Meeting.pdf") => {
        if (!htmlContent) {
            toast.error("No content to export.");
            return;
        }

        const { state_name_english, zpname, zpcode, bpname, bpcode, gpname, gpcode } = storageData || {};
        const meetingType = data?.meeting_type || "-";
        const meetingTitle = data?.meeting_title || "-";

        const element = document.createElement("div");
        element.style.width = "100%";
        element.style.maxWidth = "794px";

        element.innerHTML = `
      <!-- Header Section -->
      <div style="display:flex; align-items:center; gap:7px; margin-bottom:10px;">
        <img src="${PanchayatiRajLogo}" alt="Logo" style="height:50px;" />
        ${state_name_english ? `<div>State: ${state_name_english}</div>` : ""}
        ${zpname ? `<div>ZP: ${zpname}${zpcode ? ` (${zpcode})` : ""}</div>` : ""}
        ${bpname ? `<div>BP: ${bpname}${bpcode ? ` (${bpcode})` : ""}</div>` : ""}
        ${gpname ? `<div>GP: ${gpname}${gpcode ? ` (${gpcode})` : ""}</div>` : ""}
      </div>
      <hr/>

      <!-- Meeting Info -->
      <div style="margin-bottom: 10px;">
        <div><b>Meeting Type:</b> ${meetingType}</div>
        <div><b>Meeting Title:</b> ${meetingTitle}</div>
      </div>

      <!-- Minutes Content -->
      <div>${htmlContent}</div>
    `;

        const opt: any = {
            margin: [15, 10, 15, 10],
            filename: fileName,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        };

        html2pdf()
            .set(opt)
            .from(element)
            .save()
            .then(() => toast.success("Exported as PDF"))
            .catch((err) => {
                console.error(err);
                toast.error("Failed to export PDF.");
            });
    };

    // back button resets taskId in redux
    const handleBack = () => {
        dispatch(
            updateDashboardFields({
                menu: "Multilingual Coverage",
                fields: { taskId: "" },
            })
        );
    };

    return (
        <Wrapper>
        <h5 className="recentGramSabha-sub-heading">Recent Gram Sabha Meetings</h5>
            <div className="mt-3">
                <div className="d-flex justify-content-between mb-2">
                    <div className="d-flex mb-3">
                        <button className="back-btn me-2" onClick={handleBack}>
                            &larr; Back
                        </button>
                        <div className="data-chip me-2">{multilingual?.meeting_language_desc}</div>
                    </div>
                </div>

                <div className="">
                    {data?.meeting_type && <p className="mb-0"> <b>Metting Type : </b>{data.meeting_type}</p>}
                    {data?.meeting_title && <p> <b>Metting Title :</b> {data.meeting_title}</p>}
                </div>

                {loading && <p className="text-center">Loading...</p>}
                {error && <p className="text-danger">Error Occurred !!!</p>}
                {data?.media_type && data?.video_url && (
                    <div className="preview-area mt-3">
                        {data.media_type === "video" ? (
                            <video width="100%" height="auto" controls>
                                <source src={data.video_url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        ) : data.media_type === "audio" ? (
                            <audio controls>
                                <source src={data.video_url} type="audio/mpeg" />
                                Your browser does not support the audio tag.
                            </audio>
                        ) : null}
                    </div>
                )}

                <div className="card mt-3">
                    <div className="card-body">
                        <h4 className="text-center">
                            {!loading && !error && !data ? (
                                <p className="text-danger mb-0">No data available.</p>
                            ) : (

                                <p className="">Final Results Section</p>
                            )}
                        </h4>

{/* 
                        {summaryHtml.englishSummary && (
                            <MeetingSummary
                                title="View Minutes of the Meeting (English)"
                                htmlContent={summaryHtml.englishSummary}
                                rawContent={data?.english_minutes || ""}
                                onExportPDF={() =>
                                    handleExportPDF(summaryHtml.englishSummary, "Minutes_English.pdf")
                                }
                                isOpen={openIndex === 0}
                                onToggle={() => setOpenIndex(openIndex === 0 ? null : 0)}
                            />
                        )} */}

                        {/* {summaryHtml.regionalSummary && (
                            <MeetingSummary
                                title={`View Minutes of the Meeting (${data.translated_minutes_lang})`}
                                htmlContent={summaryHtml.regionalSummary}
                                rawContent={data?.translated_minutes || ""}
                                onExportPDF={() =>
                                    handleExportPDF(
                                        summaryHtml.regionalSummary,
                                        `Minutes_${data.translated_minutes_lang}.pdf`
                                    )
                                }
                                isOpen={openIndex === 1}
                                onToggle={() => setOpenIndex(openIndex === 1 ? null : 1)}
                            />
                        )} */}

                    </div>
                </div>

                <div className="card bg-danger-cus mt-2">
                    <div className="card-body">
                        <h5 className="card-heading text-center text-danger">DISCLAIMER</h5>
                        <p>
                            The AI-generated summaries are based on uploaded audio/video and may contain
                            errors due to input quality or language variation. Please verify the content
                            before official use. The Ministry of Panchayati Raj is not liable for any
                            discrepancies.
                        </p>
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

export default MultilingualDashboard3;
